/**
 * Authentication storage utilities for managing refresh tokens and user sessions
 */

import { createLogger } from "./logger";

const logger = createLogger("AuthStorage");

// Storage keys
const AUTH_STORAGE_KEY = "pani_auth_token";
const USER_STORAGE_KEY = "pani_user_data";

/**
 * Store refresh token in localStorage
 * @param {string} token - Refresh token to store
 */
export const storeRefreshToken = (token) => {
  try {
    if (token && typeof token === 'string') {
      logger.log("💾 Storing refresh token:", {
        tokenValue: token,
        tokenLength: token?.length,
        tokenStart: token?.substring(0, 30) + "...",
        hasBearer: token?.startsWith('Bearer '),
        isValidJWTFormat: /^Bearer\s+[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/.test(token)
      });
      
      localStorage.setItem(AUTH_STORAGE_KEY, token);
      
      // Verify storage immediately
      const storedValue = localStorage.getItem(AUTH_STORAGE_KEY);
      const storageSuccess = storedValue === token;
      
      if (storageSuccess) {
        logger.log("✅ Refresh token stored in localStorage successfully");
      } else {
        logger.error("❌ Token storage verification failed:", {
          original: token,
          stored: storedValue,
          match: storageSuccess
        });
      }
    } else {
      logger.warn("⚠️ Invalid token provided for storage:", { token, type: typeof token });
    }
  } catch (error) {
    logger.error("❌ Error storing refresh token:", error);
  }
};

/**
 * Get refresh token from localStorage
 * @returns {string|null} - Stored refresh token or null
 */
export const getStoredRefreshToken = () => {
  try {
    const token = localStorage.getItem(AUTH_STORAGE_KEY);
    if (token) {
      logger.log("✅ Refresh token retrieved from localStorage:", {
        tokenValue: token,
        tokenLength: token?.length,
        tokenStart: token?.substring(0, 30) + "...",
        hasBearer: token?.startsWith('Bearer '),
        isValidJWTFormat: /^Bearer\s+[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/.test(token)
      });
      return token;
    }
    logger.log("ℹ️ No refresh token found in localStorage");
    return null;
  } catch (error) {
    logger.error("❌ Error retrieving refresh token:", error);
    return null;
  }
};

/**
 * Store user data in localStorage (with encrypted refresh token)
 * @param {Object} userData - User data to store
 */
export const storeUserData = (userData) => {
  try {
    if (userData && typeof userData === 'object') {
      const dataToStore = {
        ...userData,
        // Ensure refresh_token is included
        timestamp: Date.now() // Add timestamp for cache validation
      };
      
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(dataToStore));
      
      // Also store just the token separately for easy access
      if (userData.refresh_token) {
        storeRefreshToken(userData.refresh_token);
      }
      
      logger.log("✅ User data stored in localStorage:", {
        hasRefreshToken: !!userData.refresh_token,
        userId: userData._id,
        keys: Object.keys(userData)
      });
    }
  } catch (error) {
    logger.error("❌ Error storing user data:", error);
  }
};

/**
 * Get user data from localStorage
 * @returns {Object|null} - Stored user data or null
 */
export const getStoredUserData = () => {
  try {
    const storedData = localStorage.getItem(USER_STORAGE_KEY);
    if (storedData) {
      const userData = JSON.parse(storedData);
      
      // Check if data is not too old (optional - 24 hours max)
      const isDataFresh = userData.timestamp && (Date.now() - userData.timestamp) < 24 * 60 * 60 * 1000;
      
      if (isDataFresh) {
        logger.log("✅ User data retrieved from localStorage:", {
          hasRefreshToken: !!userData.refresh_token,
          userId: userData._id,
          age: userData.timestamp ? Math.round((Date.now() - userData.timestamp) / 1000 / 60) + ' minutes' : 'unknown'
        });
        return userData;
      } else {
        logger.warn("⚠️ Stored user data is stale, clearing it");
        clearAuthStorage();
        return null;
      }
    }
    return null;
  } catch (error) {
    logger.error("❌ Error retrieving user data:", error);
    return null;
  }
};

/**
 * Clear all authentication data from localStorage
 */
export const clearAuthStorage = () => {
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    logger.log("✅ Authentication data cleared from localStorage");
  } catch (error) {
    logger.error("❌ Error clearing auth storage:", error);
  }
};

/**
 * Check if user is authenticated (has valid stored token)
 * @returns {boolean} - Whether user has valid stored authentication
 */
export const hasStoredAuth = () => {
  logger.log("🔍 Checking stored authentication...");
  
  const token = getStoredRefreshToken();
  const userData = getStoredUserData();
  
  const isAuthenticated = !!(token && userData && userData._id);
  
  logger.log("🔐 Authentication storage check:", {
    hasToken: !!token,
    tokenLength: token?.length,
    hasUserData: !!userData,
    hasUserId: !!(userData && userData._id),
    userDataKeys: userData ? Object.keys(userData) : [],
    isAuthenticated
  });
  
  return isAuthenticated;
};

/**
 * Update stored refresh token if user data exists
 * @param {string} newToken - New refresh token
 */
export const updateStoredToken = (newToken) => {
  if (newToken) {
    const userData = getStoredUserData();
    if (userData) {
      // Update user data with new token
      const updatedUserData = {
        ...userData,
        refresh_token: newToken,
        timestamp: Date.now()
      };
      storeUserData(updatedUserData);
    } else {
      // Just store the token
      storeRefreshToken(newToken);
    }
  }
};

/**
 * Debug function to inspect authentication state (for browser logger)
 */
export const debugAuthState = () => {
  logger.group("🔍 Authentication Debug Information");
  
  // Check localStorage items directly
  const rawToken = localStorage.getItem("pani_auth_token");
  const rawUserData = localStorage.getItem("pani_user_data");
  
  logger.log("📦 Raw localStorage items:", {
    hasRawToken: !!rawToken,
    rawTokenLength: rawToken?.length,
    hasRawUserData: !!rawUserData,
    rawUserDataLength: rawUserData?.length
  });
  
  // Parse user data
  let parsedUserData = null;
  try {
    parsedUserData = rawUserData ? JSON.parse(rawUserData) : null;
  } catch (e) {
    logger.error("❌ Error parsing user data:", e);
  }
  
  logger.log("📊 Parsed user data:", {
    hasParsedData: !!parsedUserData,
    userId: parsedUserData?._id,
    email: parsedUserData?.email,
    hasRefreshToken: !!parsedUserData?.refresh_token,
    refreshTokenLength: parsedUserData?.refresh_token?.length,
    timestamp: parsedUserData?.timestamp,
    age: parsedUserData?.timestamp ? Math.round((Date.now() - parsedUserData.timestamp) / 1000 / 60) + ' minutes' : 'unknown'
  });
  
  // Test utility functions
  const storedToken = getStoredRefreshToken();
  const storedUserData = getStoredUserData();
  const hasAuth = hasStoredAuth();
  
  logger.log("🛠️ Utility function results:", {
    getStoredRefreshToken: !!storedToken,
    getStoredUserData: !!storedUserData,
    hasStoredAuth: hasAuth
  });
  
  logger.groupEnd();
  
  return {
    rawToken,
    rawUserData,
    parsedUserData,
    storedToken,
    storedUserData,
    hasAuth
  };
};

/**
 * Debug token specifically for JWT issues
 */
export const debugToken = () => {
  logger.group("🔍 Token Debug Information");
  
  // Check raw localStorage
  const rawToken = localStorage.getItem("pani_auth_token");
  const rawUserData = localStorage.getItem("pani_user_data");
  
  logger.log("📦 Raw localStorage tokens:", {
    rawToken: rawToken,
    rawTokenLength: rawToken?.length,
    rawTokenStart: rawToken?.substring(0, 30) + "...",
    hasBearer: rawToken?.startsWith('Bearer '),
    isValidJWTFormat: rawToken ? /^Bearer\s+[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/.test(rawToken) : false
  });
  
  // Parse user data token
  let parsedToken = null;
  try {
    const userData = rawUserData ? JSON.parse(rawUserData) : null;
    parsedToken = userData?.refresh_token;
  } catch (e) {
    logger.error("❌ Error parsing user data:", e);
  }
  
  logger.log("📊 User data token:", {
    parsedToken: parsedToken,
    parsedTokenLength: parsedToken?.length,
    parsedTokenStart: parsedToken?.substring(0, 30) + "...",
    hasBearer: parsedToken?.startsWith('Bearer '),
    isValidJWTFormat: parsedToken ? /^Bearer\s+[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/.test(parsedToken) : false,
    tokensMatch: rawToken === parsedToken
  });
  
  // Test utility functions
  const storedToken = getStoredRefreshToken();
  
  logger.log("🛠️ Utility function result:", {
    utilityToken: storedToken,
    utilityTokenLength: storedToken?.length,
    utilityTokenStart: storedToken?.substring(0, 30) + "...",
    hasBearer: storedToken?.startsWith('Bearer '),
    isValidJWTFormat: storedToken ? /^Bearer\s+[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/.test(storedToken) : false
  });
  
  logger.groupEnd();
  
  return {
    rawToken,
    parsedToken,
    storedToken,
    comparison: {
      rawVsParsed: rawToken === parsedToken,
      rawVsUtility: rawToken === storedToken,
      parsedVsUtility: parsedToken === storedToken
    }
  };
};

// Make debug functions available in browser logger
if (typeof window !== 'undefined') {
  window.debugAuthState = debugAuthState;
  window.debugToken = debugToken;
}